import { Fragment, useState } from 'react';
import {
    SimpleForm, useTranslate,
    Toolbar, SaveButton, useCreate,
    useNotify, useRedirect, useRefresh, NumberInput,
    Button,
    useGetIdentity,
} from 'react-admin';

import { SectionField } from '../../components';
import { makeStyles } from '@mui/styles';
import { Description, Cancel, Send } from '@material-ui/icons';
import { Dialog, DialogActions, DialogContent, DialogTitle } from '@material-ui/core';
import io from 'socket.io-client';
import StatusProgress from '../../components/deploymentProgress';
import { useEffect, useRef } from 'react';
import { useStore } from 'react-admin';
import { Alert, Typography } from '@mui/material';

const useDeploymentSocket = (userId, BackendURL, enabled) => {
    const socketRef = useRef(null);
    const [deployments, setDeployments] = useStore('deployments', {});
    const [deploymentLogs, setDeploymentLogs] = useStore('deploymentLogs', {});

    const connectSocket = () => {
        // If we already have an open socket, or we’re not “enabled,” bail out
        if (socketRef.current?.connected || !userId || !enabled) return;

        // Create the socket but don’t auto‐connect until we’ve wired up handlers
        const socket = io(BackendURL, { autoConnect: false, query: { userId } });
        socketRef.current = socket;

        socket.on('connect', () => {
            console.log('WebSocket connection established.');
            socket.emit('joinRoom', userId);
        });

        socket.on('disconnect', () => {
            console.log('WebSocket disconnected.');
            socketRef.current = null;
        });

        socket.on('deploymentProgress', ({ progress, statusText, status, error, podId }) => {
            console.log('Received deploymentProgress:', { progress, statusText, status, error, podId });

            setDeployments(prev => ({
                ...prev,
                [userId]: { progress, statusText, status, error }
            }));

            setDeploymentLogs(prev => ({
                ...prev,
                [userId]: [
                    ...(prev[userId] || []),
                    { progress, statusText, status, error, podId }
                ]
            }));
        });

        socket.on('error', (err) => {
            console.error('Socket error:', err);
        });

        // now actually start the connection
        socket.connect();
    };

    const disconnectSocket = () => {
        if (socketRef.current) {
            socketRef.current.disconnect();
            socketRef.current = null;
        }
    };

    useEffect(() => {
        if (userId) {
            connectSocket();
            return () => disconnectSocket();
        }
    }, [userId]);

    return { connectSocket, disconnectSocket };
};


const useStyles = makeStyles((theme) => ({
    toolbar: { display: 'flex' },
    spacer: { flex: 1 },
    buttonMarginLeft: { marginLeft: 10 },
    flexBox: { width: 'auto', display: 'flex', flexDirection: 'row', alignItems: 'center' },
    inputMarginLeft: { marginLeft: 40 },
    deployButton: { display: 'flex !important', justifyContent: 'right !important' },
    color: { color: theme.palette.primary.main, backgroundColor: 'white', border: theme.palette.primary.main }
}));

const Validate = (values) => {
    const errors = {};
    if (!values.pod_max) errors.pod_max = 'ra.validation.required';
    return errors;
};

const DeployNewPodUser = (props) => {
    const [create] = useCreate();
    const notify = useNotify();
    const refresh = useRefresh();
    const classes = useStyles();
    const translate = useTranslate();

    const [showDialog, setShowDialog] = useState(false);
    //const [showProgressDialog, setShowProgressDialog] = useStore('showProgressDialog', false);
    // const [deployments, setDeployments] = useStore('deployments', {});
    // const [deploymentLogs, setDeploymentLogs] = useStore('deploymentLogs', {});
    const [socketRef, setSocketRef] = useState(null); // State to hold the socket reference
    const [deployments, setDeployments] = useState({}); // Store deployment state for each tab
    const [deploymentLogs, setDeploymentLogs] = useState({});
    const [showProgressDialog, setShowProgressDialog] = useState(false); // New dialog state

    const { data: identity, isLoading } = useGetIdentity();
    const { id: userId } = identity || {};

    const BackendURL = process.env.REACT_APP_PROGRESS_URL;

    //const { connectSocket, disconnectSocket } = useDeploymentSocket(userId, BackendURL);

    if (isLoading) return <div>{translate('app.LoadingMessage')}</div>;

    const handleClick = () => {
        setShowDialog(true);
    };

    const handleCloseClick = () => {
        setShowDialog(false);
    };

    const handleCloseProgressDialog = () => {
        //disconnectSocket();

        //setShowProgressDialog(false);

        // Clean store for current user after deployment complete
        setDeployments(prev => {
            const updated = { ...prev };
            delete updated[userId];
            return updated;
        });

        setDeploymentLogs(prev => {
            const updated = { ...prev };
            delete updated[userId];
            return updated;
        });

    };

    const handleCloseDialog = () => {
        // remove this user's data
        setDeployments(prev => {
            const { [userId]: _, ...rest } = prev;
            return rest;
        });
        setDeploymentLogs(prev => {
            const { [userId]: _, ...rest } = prev;
            return rest;
        });

        // then cleanly disconnect
        closeSocket();
    };

    const closeSocket = () => {
        // Disconnect the socket when the dialog is closed
        try {
            console.log('WebSocket connection should be disconnected.');

            if (socketRef && socketRef.connected) {
                socketRef.disconnect();
                console.log('WebSocket connection disconnected.');
            }

            setShowProgressDialog(false);
        } catch (error) {
            console.error('Error disconnecting WebSocket:', error);
        }
    };

    const handleShowProgressDialog = async () => {
        if (!userId) {
            // If userId is not available, return early
            return;
        }

        const socket = io(BackendURL, {
            query: { userId }, // Pass the userId as a query parameter
        });

        socket.on('connect', () => {
            console.log('WebSocket connection established.');
        });

        socket.on('disconnect', () => {
            console.log('WebSocket connection closed.');
        });

        socket.emit('joinRoom', userId);

        // Event handler for 'deploymentProgress' event from the backend
        socket.on('deploymentProgress', ({ userId, progress, statusText, trainingId, status, error, podId }) => {
            // Append message to history
            setDeploymentLogs(prev => {
                const currentLogs = prev[userId] || [];
                return {
                    ...prev,
                    [userId]: [...currentLogs, { progress, statusText, status, error, podId }],
                };
            });

            // Optionally: still show latest summary in a separate state if you want
            setDeployments(prev => ({
                ...prev,
                [userId]: { progress, statusText, status, error },
            }));

            setShowProgressDialog(true);
        });
        setSocketRef(socket); // Store the socket reference in state

    };


    const handleStartDeployment = (data) => {

        const current = props.realPodCount || 0;

        const roomLimits = { 97: 2, 98: 6, 99: 6 };
        const maxPods = roomLimits[props.roomNumber] || null;

        const requested = props.trainerPod ? 1 : data.pod_max;

        if (maxPods && (current + requested) > maxPods) {
            notify(
                `Deployment denied: Room C${props.roomNumber} already has ${current} pods (limit: ${maxPods}). 
                You requested ${requested} more, which would exceed the limit.`,
                { type: 'error' }
            );
            return;
        }


        handleShowProgressDialog();

        const dataToSend = {
            room: props.room,
            trainingTemplate: props.trainingTemplate,
            pod_max: props.trainerPod ? 1 : data.pod_max
        };

        create(`trainingTemplates/deployNewPodUser`, { data: dataToSend }, {
            onSuccess: () => {
                notify(translate('app.trainingTemplates.deployUserPod.successMessage'), { type: 'success' });
            },
            onError: (error) => {
                const errorMessage = `${translate('app.trainingTemplates.deployUserPod.errorMessage')}: ${error.message} 
                ${translate('app.trainingTemplates.deployUserPod.contactAdmin')}`;

                notify(errorMessage, { type: 'error' });

                // Add error to deployment logs
                setDeploymentLogs(prev => ({
                    ...prev,
                    [userId]: [
                        ...(prev[userId] || []),
                        {
                            progress: 0,
                            statusText: errorMessage,
                            status: 'error',
                            error: error.message,
                            podId: ''
                        }
                    ]
                }));

                // Optionally update deployment status too
                setDeployments(prev => ({
                    ...prev,
                    [userId]: {
                        ...prev[userId],
                        status: 'error',
                        statusText: errorMessage,
                        error: error.message
                    }
                }));
            }
        });

        // Initialize deployment state
        setDeployments(prev => ({
            ...prev,
            [userId]: { progress: 0, statusText: 'starting', status: 'starting', error: '' }
        }));

        setDeploymentLogs(prev => ({
            ...prev,
            [userId]: [{
                progress: 0, statusText: 'Starting deployment...', status: 'starting', error: '', podId: ''
            }]
        }));

        setShowProgressDialog(true);

        //connectSocket();
        handleCloseClick();
    };

    const CreateToolbar = ({ trainerPod, trainerPodDeployed }) => (
        <Toolbar className={classes.toolbar}>
            <Button
                color="primary"
                label="ra.action.cancel"
                size="medium"
                onClick={handleCloseClick}
                className={classes.color}
            >
                <Cancel />
            </Button>

            <SaveButton
                label={translate('app.buttons.deploy')}
                className={classes.buttonMarginLeft}
                alwaysEnable={
                    trainerPod && !trainerPodDeployed
                }
                disabled={trainerPod && trainerPodDeployed}
            />
        </Toolbar>
    );


    return (
        <Fragment>
            <Button label={translate('app.trainingTemplates.deployUserPod.button')} className={classes.deployButton} onClick={handleClick}>
                <Send />
            </Button>

            {/* First Dialog */}
            <Dialog fullWidth open={showDialog} onClose={handleCloseClick} aria-label={translate('app.trainingTemplates.deployUserPod.title')}>
                <DialogTitle><SectionField label={translate('app.trainingTemplates.deployUserPod.title')} icon={Send} /></DialogTitle>
                <DialogContent>
                    <SimpleForm
                        validate={props.trainerPod ? undefined : Validate}
                        toolbar={<CreateToolbar trainerPod={props.trainerPod} trainerPodDeployed={props.trainerPodDeployed} />}
                        onSubmit={handleStartDeployment}
                        redirect="list"
                    >

                        {props.trainerPod ? (
                            props.trainerPodDeployed ? (
                                // Trainer pod already deployed
                                <Alert severity="error" style={{ marginTop: 8 }}>
                                    {translate('app.trainingTemplates.deployTrainerPod.trainerPodAlreadyDeployed')}
                                    <br />
                                    {translate('app.trainingTemplates.deployUserPod.contactAdmin')}
                                </Alert>
                            ) : (
                                // Trainer pod can be deployed
                                <Alert severity="warning" style={{ marginTop: 8 }}>
                                    {translate('app.trainingTemplates.deployTrainerPod.message')}
                                </Alert>
                            )
                        ) : (
                            <>
                                <SectionField
                                    label={translate('app.trainingTemplates.deployUserPod.message')}
                                    icon={Description}
                                />
                                <NumberInput
                                    source="pod_max"
                                    min="0"
                                    label={translate('app.trainingTemplates.deployUserPod.podMax')}
                                />
                            </>

                        )}
                    </SimpleForm>

                </DialogContent>
            </Dialog>

            {/* Second Dialog */}
            {Object.keys(deployments).map((userId) => (
                <Dialog fullWidth open={showProgressDialog}
                    onClose={(event, reason) => {
                        if (reason === 'backdropClick') return; // ignore backdrop clicks
                        handleCloseDialog(deployments); // only close with ESC or manual button
                    }}
                    aria-label="Deployment Progress">
                    <DialogTitle><SectionField label='Deployment Progress' icon={Send} /></DialogTitle>
                    <DialogContent>
                        <StatusProgress deployments={deployments} deploymentLogs={deploymentLogs} userId={userId} />
                    </DialogContent>
                    <DialogActions sx={{ justifyContent: 'flex-start' }}>
                        <Button color='primary' label="ra.action.close" size="medium" onClick={handleCloseDialog} className={classes.color}>
                            <Cancel />
                        </Button>
                    </DialogActions>
                </Dialog>
            ))}
        </Fragment>
    );
};

export default DeployNewPodUser;
